/**
 * TanStack Query hooks for the Microsoft Defender board.
 *
 * **This board ships dark, and these hooks are written for that.** The four
 * Graph application permissions it needs — `SecurityIncident.Read.All`,
 * `SecurityAlert.Read.All`, `SecurityEvents.Read.All` and
 * `SecurityIdentitiesHealth.Read.All` — have not been consented yet, so the
 * expected shape of a *successful* response from the four data routes is
 * `access_state: "consent_missing"` (or `"no_credential"` where NOVA has no
 * Graph credential at all). None of those is an error: a missing credential, a
 * 403, a 404, a 400 and a 5xx are all fields on a 200
 * (`configured` / `access_state` / `status_detail` / `missing_permissions` /
 * `errors`). A thrown error from any hook here therefore means the *NOVA* API is
 * down, not Graph — worth remembering when reading an `error` branch.
 *
 * `/defender/permissions` is the exception that makes the dark state workable:
 * it touches Graph at all, so it answers with no credential, cannot be
 * throttled, and is what drives the setup panel.
 *
 * **The poll interval comes from the payload, not from a constant here.** Each
 * `/defender/*` response carries `cache_ttl_sec` — how long the backend reuses a
 * snapshot before re-reading Graph — and the four data routes deliberately
 * disagree (incidents and alerts 5 min, identity health 10 min, secure score
 * 60 min, all `NOVA_DEFENDER_*_TTL_SEC`-overridable). Polling faster than the
 * backend rebuilds just re-serves identical bytes, and a hardcoded interval
 * would silently go wrong the moment someone tunes a TTL. Same reasoning as
 * `./fo.ts`, which reads its interval off the wire for the same reason.
 *
 * A failed build is negative-cached for far less than the full TTL
 * (`ERROR_CACHE_TTL_SEC`, 30 s), so the board lights up promptly once consent
 * lands — no redeploy, no code change, and no frontend change either.
 *
 * **Nothing here is persisted.** `../persist.ts` is a deny-by-default allow-list
 * and `["defender", …]` is deliberately absent from it: security incident and
 * alert data does not belong in browser storage, even aggregated behind a
 * k-anonymity floor.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching every other hook file: the barrel re-exports
 * these hooks, so importing it here would close an import cycle.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getDefenderAlerts,
  getDefenderIdentityHealth,
  getDefenderIncidents,
  getDefenderPermissions,
  getDefenderSecureScore,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the setup panel — what the board needs, has, and never asks for. */
export const DEFENDER_PERMISSIONS_KEY = ["defender", "permissions"] as const;
/** Query key for the incident panel (14-day window). */
export const DEFENDER_INCIDENTS_KEY = ["defender", "incidents"] as const;
/** Query key for the alert-volume panel (7-day window). */
export const DEFENDER_ALERTS_KEY = ["defender", "alerts"] as const;
/** Query key for Microsoft Secure Score (90 daily snapshots). */
export const DEFENDER_SECURE_SCORE_KEY = ["defender", "secure-score"] as const;
/** Query key for Defender-for-Identity sensor and agent health (point-in-time). */
export const DEFENDER_IDENTITY_HEALTH_KEY = ["defender", "identity-health"] as const;

/**
 * Interval used only before the first response arrives, in ms.
 *
 * The shortest TTL any Defender route ships with, so the first refetch can never
 * be slower than the backend's own fastest rebuild. After that the payload's own
 * `cache_ttl_sec` takes over.
 */
const FALLBACK_INTERVAL_MS = 300_000;

/**
 * How often the setup panel re-reads, in ms.
 *
 * `/defender/permissions` reports `cache_ttl_sec: 0` — it holds no cache because
 * it makes no Graph call — so there is no TTL on the wire to follow. It is
 * polled on its own short cadence instead, because what it reports *does*
 * change without a request from here: it reflects what the four panels' reads
 * have observed, so a permission moving from `not_probed` to `granted` or
 * `denied` shows up here on the next tick.
 */
const PERMISSIONS_INTERVAL_MS = 120_000;

/**
 * Refetch on the cadence the payload itself declares.
 *
 * Takes the *data* rather than the query so it cannot feed back into TanStack's
 * inference of the query's own data type.
 */
function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/**
 * The board's setup panel: the four permissions it asks for, what a read has
 * actually proved about each, and what it deliberately never asks for.
 *
 * Makes no Graph call, so it answers in every state — including with no
 * credential at all — and is the panel the board renders while it is dark.
 */
export function useDefenderPermissions() {
  return useQuery({
    queryKey: DEFENDER_PERMISSIONS_KEY,
    queryFn: async () => {
      const { data } = await getDefenderPermissions();
      logger.debug("[defender] permissions received", {
        grantedCount: data?.granted_count,
        required: data?.required.length ?? 0,
        missing: data?.missing_permissions.length ?? 0,
      });
      return data;
    },
    refetchInterval: PERMISSIONS_INTERVAL_MS,
  });
}

/**
 * Open incidents by severity, age and trend over 14 days.
 *
 * Counts and mixes only — the payload has no field for an incident's display
 * name, assignee or evidence, because a Defender-composed incident name
 * routinely contains a device or account name.
 */
export function useDefenderIncidents() {
  return useQuery({
    queryKey: DEFENDER_INCIDENTS_KEY,
    queryFn: async () => {
      const { data } = await getDefenderIncidents();
      // Counts and states only. Never spread a security payload into a log line.
      logger.debug("[defender] incidents received", {
        accessState: data?.access_state,
        openIncidents: data?.open_incidents,
        truncated: data?.truncated,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/** Alert volume and its mix by product, detection source, category and technique. */
export function useDefenderAlerts() {
  return useQuery({
    queryKey: DEFENDER_ALERTS_KEY,
    queryFn: async () => {
      const { data } = await getDefenderAlerts();
      logger.debug("[defender] alerts received", {
        accessState: data?.access_state,
        alertsTotal: data?.alerts_total,
        truncated: data?.truncated,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Microsoft Secure Score: today's figure, 90 daily snapshots and the controls
 * leaving the most points on the table.
 *
 * The only Defender panel with real history behind it — Microsoft keeps daily
 * scores — so the only one this board can honestly draw as a trend.
 */
export function useDefenderSecureScore() {
  return useQuery({
    queryKey: DEFENDER_SECURE_SCORE_KEY,
    queryFn: async () => {
      const { data } = await getDefenderSecureScore();
      logger.debug("[defender] secure score received", {
        accessState: data?.access_state,
        scorePct: data?.score_pct,
        trendPoints: data?.trend.length ?? 0,
        controls: data?.worst_controls.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Defender-for-Identity sensor and agent health, as counts.
 *
 * Expect `access_state: "not_provisioned"` rather than zeros if MDI is not
 * deployed — a 404 is a fact about the estate, and a different ticket from
 * consent.
 */
export function useDefenderIdentityHealth() {
  return useQuery({
    queryKey: DEFENDER_IDENTITY_HEALTH_KEY,
    queryFn: async () => {
      const { data } = await getDefenderIdentityHealth();
      logger.debug("[defender] identity health received", {
        accessState: data?.access_state,
        state: data?.state,
        sensorsTotal: data?.sensors_total,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}
